import { useTranslation } from "react-i18next";

import { Text } from "@/components/ui/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { CutReadStrength } from "@digital-twins/shared-types/reporting";

interface ReadStrengthTagProps {
  readStrength: CutReadStrength;
  dataTestId: string;
}

/** The pill and tooltip keys of each read strength (`studyReport.storyline.read.*`). */
const READ_STRENGTH_KEYS = {
  significant: {
    label: "studyReport.storyline.read.significant",
    hint: "studyReport.storyline.read.significantHint",
  },
  directional: {
    label: "studyReport.storyline.read.directional",
    hint: "studyReport.storyline.read.directionalHint",
  },
  no_clear_difference: {
    label: "studyReport.storyline.read.no_clear_difference",
    hint: "studyReport.storyline.read.no_clear_differenceHint",
  },
  too_few: {
    label: "studyReport.storyline.read.too_few",
    hint: "studyReport.storyline.read.too_fewHint",
  },
} as const satisfies Record<CutReadStrength, { label: string; hint: string }>;

/**
 * The read strength the code classified, as a neutral muted pill with its
 * meaning on hover. No good or bad colour ("No real difference" is often
 * the finding) and never a p-value.
 */
export function ReadStrengthTag(props: ReadStrengthTagProps) {
  const { readStrength, dataTestId } = props;

  const { t } = useTranslation("app");

  const keys = READ_STRENGTH_KEYS[readStrength];

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={dataTestId}
            data-read-strength={readStrength}
            className="inline-flex shrink-0 items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t(keys.label)}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px]">
          <Text size="sm">{t(keys.hint)}</Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
